/**
 * "Working files" disclosure (spec §3.8): a collapsible list of a turn's intermediate run artifacts under the
 * assistant response — filename + type chip, no viewer.
 *
 * DATA-GATE (APP-3368): the wire has NO field distinguishing intermediate/working artifacts from primary ones
 * — verified end-to-end (op-summarize `api_models.py` Artifact union, pi-server `helpers/ai.js` mapArtifact,
 * client artifact-contract) — so this renders dark today. The agreed gate is an `intermediate === true` flag on
 * the artifact envelope; once the AI team emits it, the disclosure lights up automatically (same honesty
 * pattern as the pin gate). Until then `getWorkingFiles` returns `[]` for every real turn.
 */

import { useState } from "react"
import { Box, Chip, Collapse, List, ListItem, ListItemText, Typography, styled } from "@mui/material"
import type { SxProps, Theme } from "@mui/material"
import { KeyboardArrowRight } from "@mui/icons-material"
import type { ChatDataMessagePart, ChatMessage, ChatMessagePart } from "@mui/x-chat-headless"
import { type ArtifactEnvelope, isArtifactEnvelope } from "./artifact-contract"

export interface WorkingFile {
  id: string
  title: string
  type: string
}

/** Stable empty result so a working-files-free turn never allocates on the (per-token) hot path. */
export const EMPTY_WORKING_FILES: readonly WorkingFile[] = []

/** The intermediate-artifact gate: an envelope explicitly flagged `intermediate: true` (no wire source yet). */
export function isIntermediateArtifact(value: unknown): value is ArtifactEnvelope & { intermediate: true } {
  return isArtifactEnvelope(value) && value.intermediate === true
}

function isDataArtifactPart(part: ChatMessagePart): part is ChatDataMessagePart {
  return part.type === "data-artifact"
}

/** Intermediate artifacts carried by a turn's `data-artifact` parts, mapped to disclosure rows. */
export function getWorkingFiles(message: ChatMessage): readonly WorkingFile[] {
  const files = message.parts
    .filter(isDataArtifactPart)
    .map((part) => part.data)
    .filter(isIntermediateArtifact)
    .map((artifact) => ({ id: artifact.id, title: artifact.title ?? artifact.id, type: artifact.type }))
  return files.length > 0 ? files : EMPTY_WORKING_FILES
}

const ROOT_SX = { mt: 0.5 } satisfies SxProps<Theme>
const CHIP_SX = { textTransform: "none" } satisfies SxProps<Theme>
const CARET_SX = { transition: "transform 120ms" } satisfies SxProps<Theme>
const CARET_OPEN_SX = { transform: "rotate(90deg)" } satisfies SxProps<Theme>

const SummaryButton = styled("button")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(0.5),
  padding: theme.spacing(0.25, 0.5),
  margin: 0,
  border: 0,
  background: "none",
  cursor: "pointer",
  color: theme.palette.text.secondary,
  font: "inherit",
  borderRadius: theme.shape.borderRadius,
  "&:hover": { color: theme.palette.text.primary },
}))

export function WorkingFilesDisclosure({ files }: { files: readonly WorkingFile[] }) {
  const [isOpen, setIsOpen] = useState(false)
  if (files.length === 0) return null
  return (
    <Box sx={ROOT_SX} data-testid="chatbox-working-files">
      <SummaryButton type="button" aria-expanded={isOpen} data-testid="chatbox-working-files-toggle" onClick={() => setIsOpen((open) => !open)}>
        <KeyboardArrowRight sx={[CARET_SX, isOpen && CARET_OPEN_SX]} fontSize="small" aria-hidden />
        <Typography variant="caption">Working files ({files.length})</Typography>
      </SummaryButton>
      <Collapse in={isOpen} unmountOnExit>
        <List dense disablePadding>
          {files.map((file) => (
            <ListItem
              key={file.id}
              data-testid="chatbox-working-file"
              secondaryAction={<Chip size="small" variant="outlined" label={file.type} sx={CHIP_SX} />}
            >
              <ListItemText primary={file.title} slotProps={{ primary: { noWrap: true, variant: "body2" } }} />
            </ListItem>
          ))}
        </List>
      </Collapse>
    </Box>
  )
}
